import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import useGAData from "app/pages/activation/queries/googleAnalytics/useGAData";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { GAWidgetTexts } from "../../components/GAWidget/texts";
import { dateFormats } from "../../constants/dates";
import type { FirstPartyDataSource } from "../../types/api";

const useGAWidgetData = (
    activationId: string,
    granularity: GranularityNames,
    enabled: boolean,
    source?: FirstPartyDataSource,
) => {
    const [metricData, setMetricData] = useState({});

    const { data, isLoading, fetchStatus, isFetching, isSuccess, isError, gaErrorKind, refetch } = useGAData(
        activationId,
        granularity,
        enabled,
        source,
    );

    useEffect(() => {
        if (isSuccess && data) {
            const keys = Object.keys(data);
            const result = {};

            keys.forEach((key: string) => {
                const current = data[key];

                if (current?.trend) {
                    const trend = current?.trend
                        .filter((item) => item?.value !== null)
                        .sort((a, b) => dayjs(a.key).valueOf() - dayjs(b.key).valueOf())
                        .map((item) => [dayjs(item.key).format(dateFormats.shortMonthDayYear), item.value]);

                    result[key] = { trend, absolute: current.absolute || null, change: current.change || null };
                } else {
                    const isEmpty = current.desktop === 0 || current.mobile === 0;

                    const chartData = {
                        data: [
                            {
                                color: colorsPalettes.blue[400],
                                id: "desktop",
                                name: translate(GAWidgetTexts.distributionDesktop),
                                total: current.desktop,
                                y: current.desktop,
                            },
                            {
                                color: colorsPalettes.blue[200],
                                id: "mobile",
                                name: translate(GAWidgetTexts.distributionMobile),
                                total: current.mobile,
                                y: current.mobile,
                            },
                        ],
                    };

                    result[key] = { ...current, chartData, isEmpty };
                }
            });

            setMetricData(result);
        }
    }, [isSuccess, data]);

    return {
        metricData,
        isLoading: isFetching || (isLoading && fetchStatus !== "idle"),
        isError,
        gaErrorKind,
        refetch,
    };
};

export default useGAWidgetData;
